import MapView from "./MapView"
import { findRoute2g, route2gEq } from "@/reducers/routes2gSlice"
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable"
import RouteStopList from "@/components/RouteStopList"
import { Button } from "@/components/ui/button"
import { AlertCircleIcon, ArrowLeft, X } from "lucide-react"
import * as React from "react"
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { useAppSelector } from "@/reducers/hooks"
import { APIProvider } from "@vis.gl/react-google-maps"
import { AvoidType, LoadType } from "@/api/adHocCommands"
import { Spinner } from "@blueprintjs/core"
import AvoidSelect from "@/components/transportOrder/AvoidSelect"
import LoadTypeSelect from "@/components/transportOrder/LoadTypeSelect"
import { ScrollArea } from "@/components/ui/scroll-area"
import { MessageOptions } from "@/reducers/chatsSliceThunks"
import { XmppContext } from "@/stream/xmppClient"
import { Alert, AlertDescription } from "../ui/alert"
import { AlertTitle } from "@/components/ui/alert"
import { selectAccountChat } from "@/reducers/accountChatsSelectors"
import { findProfile } from "@/reducers/profilesSliceSelectors"
import { RotatingTextShimmer } from "@/components/ui/rotating-text-shimmer"
import AnalyzeOrderSkeleton from "@/components/maps/AnalyzOrderSkeleton"
import { extractVhostFromUser } from "@/jidUtils"
import { useAnalyzeOrderContext } from "@/contexts/AnalyzeOrderContext"
import {
  generateRouteMessage,
  updateDistanceInMessage,
} from "@/components/analyzeOrderOnPage/utils"
import useEscapeKey from "@/hooks/useEscapeKey"
import { useMapToaster } from "@/hooks/useMapToaster"
import AnalyzeOrderMessageBox from "@/components/maps/AnalyzeOrderMessageBox"
import useTemporaryRoute from "@/hooks/useTemporaryRoute"
import { useEventCallback } from "usehooks-ts"
import { getOptionsSummaryText } from "@/components/maps/RouteOptionsSelect"
import {
  selectIsHereRouting,
  selectRoutingProvider,
} from "@/reducers/serviceConfigSelectors"
import AlternativeLeg from "./AlternativeLeg"
import AlternativeRouteSelector from "./AlternativeRouteSelector"
import CollapsibleVehicleTollInfo from "./CollapsibleVehicleTollInfo"
import HoveredRouteHighlight from "./HoveredRouteHighlight"
import DriverCurrentRoute from "./DriverCurrentRoute"
import { useFeatureFlagEnabled } from "posthog-js/react"
import { useNaviConfig } from "@/hooks/useNaviConfig"
import { useTranslation } from "react-i18next"
import i18n from "@/lib/i18n/i18n"

type AnalyzeOrderMapProps = {
  onSend: (text: string, files: FileList, options?: MessageOptions) => void
}

const AnalyzeOrderMap: React.FC<AnalyzeOrderMapProps> = ({
  onSend,
}: AnalyzeOrderMapProps) => {
  const { t } = useTranslation()
  const [isContentReady, setIsContentReady] = useState<boolean>(false)
  const alternativeRoutesEnabled = Boolean(
    useFeatureFlagEnabled("alternative-routes"),
  )
  const hideDistanceLine = useFeatureFlagEnabled(
    "hide-distance-line-in-route-message",
  )
  // Beta: add the approach leg ("dolot") distance to the order message.
  const showApproachDistance = Boolean(
    useFeatureFlagEnabled("show-approach-distance-in-route-message"),
  )
  const {
    driver,
    routeJid,
    carConfig,
    isModalHidden,
    shouldSkipVerifyStep,
    routeData,
    messageId,
    files,
    isRetrying,
    messageGenerated,
    messageText,
    setMessageText,
    setMessageGenerated,
    isFailed,
    handleCloseMap,
    retryProcessing,
    handleBackToVerify,
    isLoading,
    hideCoordinates,
    hideStopTimes,
    showFixedTimes,
    baseStopIds,
  } = useAnalyzeOrderContext()

  const typedRouteJid = routeJid!

  const {
    temporaryRoute,
    setTemporaryRoute,
    temporaryRouteInfo,
    setTemporaryRouteInfo,
    isFetchingPolylines,
    resetTemporaryRoute,
    setInitialTemporaryRoute,
    selectedAvoid,
    selectedLoadType,
    setSelectedLoadType,
    setSelectedAvoid,
    alternativeRoutes,
    setAlternativeRoutes,
    setPerLegAlternatives,
    replaceStopWithAlternativeLeg,
    handleTemporaryRouteUpdate,
    uniqueAlternativeLegs,
    routeErrors,
    clearRouteErrors,
  } = useTemporaryRoute(typedRouteJid, messageId, {
    alternativeRoutesEnabled,
    baseStopIds,
  })

  const accountChat = useAppSelector(selectAccountChat(typedRouteJid))
  const driverProfile = useAppSelector(findProfile(accountChat?.driverA))
  const currentRoute = useAppSelector(findRoute2g(typedRouteJid), route2gEq)

  const name = driverProfile
    ? `${driverProfile.firstName}`
    : driver
      ? `${driver.firstName}`
      : ""

  const { myJid } = useContext(XmppContext)
  let vhost = ""
  try {
    vhost = extractVhostFromUser(myJid?.toString())
  } catch (e) {
    console.error(e)
  }
  const { configured: naviConfigured, loading: naviLoading } =
    useNaviConfig(vhost)
  const isMarcosMode = naviConfigured
  // Ref to track if this is the first distance update after message generation
  const isInitialDistanceUpdate = useRef(true)

  // Reset content state when retry starts
  useEffect(() => {
    if (isRetrying) {
      setIsContentReady(false)
      resetTemporaryRoute()
      setMessageGenerated(false)
      isInitialDistanceUpdate.current = true
    }
  }, [isRetrying, resetTemporaryRoute, setMessageGenerated])

  useEffect(() => {
    setSelectedAvoid(carConfig?.avoid || [])
    setSelectedLoadType(carConfig?.loadType || [])
  }, [carConfig, setSelectedAvoid, setSelectedLoadType])

  // When the selected route changes (including initial load), sync selectedAvoid
  // with the route's car_route_config_avoid so the filter reflects the chosen route.
  useEffect(() => {
    if (!alternativeRoutes?.length || !temporaryRouteInfo) return
    const currentRouteId = temporaryRouteInfo.routes[0]?.id
    const currentRoute = alternativeRoutes.find(
      (alt) => alt.type === currentRouteId,
    )
    if (currentRoute?.carRouteConfigAvoid !== undefined) {
      setSelectedAvoid(currentRoute.carRouteConfigAvoid)
    }
  }, [alternativeRoutes, temporaryRouteInfo, setSelectedAvoid])

  // Update route data when it becomes available
  useEffect(() => {
    if (!isLoading && routeData) {
      const newTemporaryRoute = {
        stops: routeData.stops,
        jid: routeData.jid,
        lastUpdatedBy: routeData.lastUpdatedBy,
        tolls: routeData.route.tolls,
      }
      // Only set alternatives when the feature is enabled
      if (alternativeRoutesEnabled) {
        setPerLegAlternatives(routeData.perLegAlternatives)
        setAlternativeRoutes(routeData.alternativeRoutes)
      }
      setInitialTemporaryRoute(newTemporaryRoute, {
        ...routeData.route,
      })

      const newTemporaryRouteHasStops =
        newTemporaryRoute.stops && newTemporaryRoute.stops.length > 0
      setIsContentReady(true)

      // Generate message only when not generated yet, we have stops, and
      // the feature flag has resolved (otherwise we'd lock in a message using
      // the default value while PostHog is still fetching flags).
      if (
        !messageGenerated &&
        newTemporaryRouteHasStops &&
        hideDistanceLine !== undefined
      ) {
        setMessageText(
          generateRouteMessage({
            route: newTemporaryRoute,
            currentRoute,
            driverName: name,
            preferredLanguage: driverProfile?.preferredLanguage,
            routeData,
            vhost,
            hideCoordinates,
            hideDistanceLine,
            hideStopTimes,
            showFixedTimes,
            showApproachDistance,
          }),
        )
        setMessageGenerated(true)
      }
    } else if (isFailed) {
      window.analytics.track("AnalyzeOrderFailed", {
        reason: "processing_failed",
        fileNames: files?.map((uploadedFile) => uploadedFile.name),
        fileUrls: files?.map((uploadedFile) => uploadedFile.url),
        messageId: messageId,
      })
      // i18n.t, not the hook's t: a language switch must not re-run this
      // effect (it would reset the planned route).
      window.appToaster.show({
        message: i18n.t("orders.analyzeOrderMap.processFailedToast"),
        intent: "danger",
        action: retryProcessing
          ? {
              text: i18n.t("common.retry"),
              onClick: retryProcessing,
            }
          : undefined,
      })
      setIsContentReady(true)
    }
  }, [
    isLoading,
    routeData,
    isFailed,
    files,
    retryProcessing,
    messageId,
    name,
    setMessageGenerated,
    setMessageText,
    driverProfile?.preferredLanguage,
    vhost,
    setInitialTemporaryRoute,
    hideCoordinates,
    setAlternativeRoutes,
    setPerLegAlternatives,
    alternativeRoutesEnabled,
    hideDistanceLine,
    hideStopTimes,
    showFixedTimes,
    showApproachDistance,
  ])

  // Update distance in message when route changes (after initial message generation)
  useEffect(() => {
    // Skip the first render after message is generated to avoid double-processing
    if (isInitialDistanceUpdate.current && messageGenerated) {
      isInitialDistanceUpdate.current = false
      return
    }

    // Only update distance if message was already generated and we have stops
    if (
      messageGenerated &&
      messageText &&
      temporaryRoute?.stops &&
      temporaryRoute.stops.length > 0
    ) {
      const updatedMessage = updateDistanceInMessage(
        messageText,
        temporaryRoute.stops,
        currentRoute,
        driverProfile?.preferredLanguage,
        showApproachDistance,
      )
      if (updatedMessage !== messageText) {
        setMessageText(updatedMessage)
      }
    }
  }, [
    temporaryRoute?.stops,
    messageGenerated,
    messageText,
    setMessageText,
    currentRoute,
    driverProfile?.preferredLanguage,
    showApproachDistance,
  ])

  const [hoveredRouteId, setHoveredRouteId] = useState<string | undefined>(
    undefined,
  )

  // Drag state - tracks when user is dragging the route line to add passthrough
  const [isDraggingRoute, setIsDraggingRoute] = useState(false)

  const routeContainerRef = useRef<HTMLDivElement>(null)

  const handleAvoidSelectClose = useEventCallback(
    async (newAvoid: AvoidType[]) => {
      const hasChanged =
        selectedAvoid.length !== newAvoid.length ||
        !selectedAvoid.every((item) => newAvoid.includes(item))
      if (temporaryRoute && hasChanged) {
        await handleTemporaryRouteUpdate(
          temporaryRoute,
          {
            added: [],
            removed: [],
            modified: [],
            hasNonPassthroughChanges: false,
            hasPassthroughChanges: false,
            hasAvoidChanges: true,
            hasLoadTypeChanges: false,
            hasOrderChanges: false,
            hasActionOnlyChanges: false,
          },
          {
            avoid: newAvoid,
            loadType: selectedLoadType,
          },
        )
      }
    },
  )

  const handleLoadTypeSelectClose = useEventCallback(
    async (newLoadType: LoadType[]) => {
      const hasChanged =
        selectedLoadType.length !== newLoadType.length ||
        !selectedLoadType.every((item) => newLoadType.includes(item))
      if (temporaryRoute && hasChanged) {
        await handleTemporaryRouteUpdate(
          temporaryRoute,
          {
            added: [],
            removed: [],
            modified: [],
            hasNonPassthroughChanges: false,
            hasPassthroughChanges: false,
            hasAvoidChanges: false,
            hasLoadTypeChanges: true,
            hasOrderChanges: false,
            hasActionOnlyChanges: false,
          },
          {
            avoid: selectedAvoid,
            loadType: newLoadType,
          },
        )
      }
    },
  )

  useEscapeKey(handleCloseMap)
  useMapToaster(routeContainerRef.current)

  const handleBack = useEventCallback(() => {
    if (routeData) {
      if (temporaryRoute && temporaryRoute.stops && temporaryRouteInfo) {
        handleBackToVerify({
          stops: temporaryRoute?.stops,
          jid: temporaryRoute?.jid,
          lastUpdatedBy: temporaryRoute?.lastUpdatedBy,
          route: temporaryRouteInfo,
          avoidOptions: selectedAvoid,
          loadType: selectedLoadType,
          alternativeRoutes: alternativeRoutes,
        })
      }
    } else {
      handleCloseMap()
    }
  })

  const routingProvider = useAppSelector(selectRoutingProvider)
  const isHereRouting = useAppSelector(selectIsHereRouting)
  const summaryText = getOptionsSummaryText({
    stops: temporaryRoute?.stops,
    avoid: selectedAvoid,
    loadType: selectedLoadType,
    routingProvider,
  })
  const inProduct = Boolean(window.XMPP && !window.anonymousXMPP)

  const handleRouteSelect = useCallback(
    (routeType: string) => {
      const selectedAlternative = alternativeRoutes?.find(
        (alt) => alt.type === routeType,
      )
      if (
        temporaryRoute &&
        selectedAlternative &&
        selectedAlternative.stops &&
        selectedAlternative.routeInfo
      ) {
        setTemporaryRoute({
          ...temporaryRoute,
          stops: selectedAlternative.stops,
          routeId: selectedAlternative.type,
        })
        setTemporaryRouteInfo(selectedAlternative.routeInfo)

        window.analytics.track("AnalyzeOrderAlternativeRouteSelected", {
          messageId,
          routeType,
          distance: selectedAlternative.routeInfo.routes[0]?.distance,
          fuel: selectedAlternative.fuel,
        })
      }
    },
    [
      alternativeRoutes,
      messageId,
      setTemporaryRoute,
      setTemporaryRouteInfo,
      temporaryRoute,
    ],
  )

  const handleRouteHover = useCallback((routeId: string | undefined) => {
    setHoveredRouteId(routeId)
  }, [])

  // Handle route drag state changes from MapView
  const handleRouteDragChange = useCallback((isDragging: boolean) => {
    setIsDraggingRoute(isDragging)
  }, [])

  const handleAlternativeLegClick = useCallback(
    async (route_handle: string, legIndex: number) => {
      await replaceStopWithAlternativeLeg(route_handle, legIndex)
    },
    [replaceStopWithAlternativeLeg],
  )

  // Determine which legs have passthrough stops (hide alternatives for these legs)
  // In hook context, leg 0 = from implicit origin to first non-passthrough stop,
  // so we check for passthroughs before np[0] using -1 as the start index.
  const legsWithPassthroughs = useMemo(() => {
    if (!temporaryRoute?.stops || temporaryRoute.stops.length < 2)
      return new Set<number>()

    const stops = temporaryRoute.stops
    const result = new Set<number>()

    const nonPassthroughStopIndices: number[] = []
    stops.forEach((stop, index) => {
      if (stop.action !== "passthrough") {
        nonPassthroughStopIndices.push(index)
      }
    })

    for (let leg = 0; leg < nonPassthroughStopIndices.length; leg++) {
      const startIdx = leg > 0 ? nonPassthroughStopIndices[leg - 1] : -1
      const endIdx = nonPassthroughStopIndices[leg]
      if (endIdx - startIdx > 1) {
        result.add(leg)
      }
    }

    return result
  }, [temporaryRoute?.stops])

  const selectedRouteId = temporaryRouteInfo?.routes[0].id

  const hoverRouteVisible = Boolean(
    hoveredRouteId && hoveredRouteId !== selectedRouteId && !isDraggingRoute,
  )

  // Hide alternatives when dragging route to add passthrough
  const showAlternatives =
    alternativeRoutesEnabled &&
    !isDraggingRoute &&
    !isFetchingPolylines &&
    !isMarcosMode
  return (
    <APIProvider apiKey={import.meta.env.REACT_APP_GOOGLE_MAP_KEY}>
      <div
        className={`${inProduct ? "left-[49px] w-[calc(100vw-3rem)]" : ""} bg:white fixed inset-0 z-[100] h-screen w-screen dark:bg-gray-900 ${isModalHidden ? "hidden" : ""}`}
      >
        <ResizablePanelGroup
          direction="horizontal"
          className="max-w-screen min-h-screen"
        >
          <ResizablePanel
            className="relative flex flex-col bg-[var(--panel-color)]"
            defaultSize={35}
            maxSize={50}
            minSize={25}
          >
            {isFetchingPolylines && (
              <div className="absolute inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
                <div className="flex flex-col items-center space-y-4">
                  <Spinner size={25} />
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    {t("orders.analyzeOrderMap.fetchingRoute")}
                  </p>
                </div>
              </div>
            )}
            <div className="flex w-full flex-col p-4 pb-2">
              <div className="flex items-center justify-between">
                <Button
                  variant="ghost"
                  onClick={handleBack}
                  className="self-start pl-0 text-sm"
                  disabled={!isContentReady || isFetchingPolylines}
                >
                  <ArrowLeft className="h-4 w-4" />
                  {t("common.back")}
                </Button>
              </div>
              <h2 className="mb-1 mt-4 font-semibold text-muted-foreground">
                {t("orders.analyzeOrderMap.step")}
              </h2>
              <h1 className="text-2xl font-bold">
                {t("orders.analyzeOrderMap.title")}
              </h1>
            </div>
            <div
              ref={routeContainerRef}
              className="relative z-10 flex w-full flex-1 flex-col overflow-hidden bg-[var(--panel-color)]"
            >
              {!isLoading && isFailed ? (
                <div className="flex w-full flex-col p-4">
                  <Alert variant="destructive">
                    <AlertCircleIcon />
                    <AlertTitle>
                      {t("orders.analyzeOrderMap.processFailed.title")}
                    </AlertTitle>
                    <AlertDescription>
                      <p>
                        {t("orders.analyzeOrderMap.processFailed.description")}
                      </p>
                      <ul className="list-inside list-disc text-sm">
                        <li>
                          {t(
                            "orders.analyzeOrderMap.processFailed.checkContent",
                          )}
                        </li>
                        <li>
                          {t(
                            "orders.analyzeOrderMap.processFailed.checkFormat",
                          )}
                        </li>
                      </ul>
                    </AlertDescription>
                  </Alert>
                </div>
              ) : null}

              {routeErrors ? (
                <div className="relative flex w-full flex-col p-4">
                  <Alert variant="destructive">
                    <AlertCircleIcon />
                    <AlertTitle>
                      {t("orders.analyzeOrderMap.routeFetchFailed")}
                    </AlertTitle>
                    <AlertDescription>
                      {routeErrors.map((msg, i) => (
                        <p key={i}>{msg}</p>
                      ))}
                    </AlertDescription>
                  </Alert>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-5 top-5 h-6 w-6 text-destructive hover:text-destructive"
                    onClick={clearRouteErrors}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : null}

              {!isContentReady ? (
                <div className="flex h-full w-full flex-col space-y-4 py-4">
                  {shouldSkipVerifyStep && (
                    <RotatingTextShimmer
                      className="mb-6 px-4"
                      messages={[
                        t("orders.analyzeOrderMap.loading.parameters"),
                        t("orders.analyzeOrderMap.loading.options"),
                        t("orders.analyzeOrderMap.loading.optimizing"),
                      ]}
                    />
                  )}
                  {shouldSkipVerifyStep && <AnalyzeOrderSkeleton />}
                </div>
              ) : (
                isContentReady && (
                  <div className="flex h-full w-full flex-col gap-2">
                    {!isMarcosMode && (
                      <div className="flex w-full flex-wrap gap-2 px-4 py-2">
                        <AvoidSelect
                          onClose={handleAvoidSelectClose}
                          initialSelected={selectedAvoid}
                        />
                        <LoadTypeSelect
                          onClose={handleLoadTypeSelectClose}
                          initialSelected={selectedLoadType}
                        />
                      </div>
                    )}
                    {/* Vehicle type info */}
                    {carConfig && (
                      <div className="flex w-full gap-2 px-4 *:flex-1">
                        <CollapsibleVehicleTollInfo
                          config={carConfig}
                          tolls={temporaryRouteInfo?.routes[0].tolls}
                          showFuel={isHereRouting}
                        />
                      </div>
                    )}

                    <div className="flex min-h-0 flex-1 flex-col">
                      <ScrollArea className="flex-1 px-4">
                        {/* Route options summary */}
                        {summaryText && (
                          <div className="mb-2">
                            <div className="rounded-md border border-blue-200 bg-blue-50 p-3 dark:border-blue-600 dark:bg-blue-900/20">
                              <p className="text-sm text-blue-800 dark:text-blue-200">
                                {summaryText}
                              </p>
                            </div>
                          </div>
                        )}
                        <div className="w-full space-y-4 pb-4">
                          <RouteStopList
                            driverJid={typedRouteJid}
                            isReadOnlyMap={isMarcosMode}
                            isAnalyzeOrderMap
                            routeFromAnalyzeOrder={temporaryRoute}
                            temporaryAvoidOptions={selectedAvoid}
                            onTemporaryRouteUpdate={handleTemporaryRouteUpdate}
                          />
                        </div>
                      </ScrollArea>
                      {/* Message input area - fixed to bottom. In Marcos mode
                          the same box is shown but its primary action becomes
                          "Copy message" (the order is completed in the TMS). */}
                      {!naviLoading && (
                        <AnalyzeOrderMessageBox
                          onClose={handleCloseMap}
                          route={temporaryRoute}
                          onSend={onSend}
                          avoid={selectedAvoid}
                          loadType={selectedLoadType}
                          vhost={vhost}
                          showApproachDistance={showApproachDistance}
                        />
                      )}
                    </div>
                  </div>
                )
              )}
            </div>
          </ResizablePanel>
          <ResizableHandle withHandle className="z-[1001]" />
          <ResizablePanel defaultSize={75}>
            <div className="relative z-[1000] flex h-full items-center justify-center">
              {isMarcosMode && (
                <div
                  className="absolute inset-0 z-[1002] cursor-not-allowed bg-black/5"
                  aria-hidden
                />
              )}
              <MapView
                isAnalyzeOrderMap
                temporaryRoute={isContentReady ? temporaryRoute : undefined}
                temporaryRouteInfo={
                  isContentReady ? temporaryRouteInfo : undefined
                }
                temporaryAvoidOptions={
                  isContentReady ? selectedAvoid : undefined
                }
                temporaryLoadType={
                  isContentReady ? selectedLoadType : undefined
                }
                onTemporaryRouteUpdate={handleTemporaryRouteUpdate}
                onRouteDragChange={handleRouteDragChange}
                driverJid={typedRouteJid}
                isFullScreen
                isReadOnlyMap={!isContentReady}
              >
                {/* Render unique alternative legs (deduplicated by route_handle) - only when feature is enabled and not dragging */}
                {showAlternatives &&
                  uniqueAlternativeLegs
                    .filter(
                      (legData) => !legsWithPassthroughs.has(legData.legIndex),
                    )
                    .map((legData) => (
                      <AlternativeLeg
                        key={legData.id}
                        leg={legData.leg}
                        legIndex={legData.legIndex}
                        id={legData.id}
                        visible={true}
                        hoverRouteVisible={hoverRouteVisible}
                        onLegClick={handleAlternativeLegClick}
                        routeLabel={legData.routeId}
                      />
                    ))}
                {showAlternatives && hoverRouteVisible && hoveredRouteId && (
                  <HoveredRouteHighlight
                    routeId={hoveredRouteId}
                    alternativeRoutes={alternativeRoutes}
                  />
                )}
                {/* Alternative Route Selector - only when feature is enabled and not dragging */}
                {showAlternatives && (
                  <AlternativeRouteSelector
                    selectedRouteId={temporaryRouteInfo?.routes[0].id}
                    alternativeRoutes={alternativeRoutes}
                    onRouteSelect={handleRouteSelect}
                    onRouteHover={handleRouteHover}
                    hoveredRouteId={hoveredRouteId}
                    className="mb-4"
                    showFuel={isHereRouting}
                  />
                )}
                {/* Driver's current route (original stops) - rendered LAST to be on top */}
                {showAlternatives && temporaryRoute?.stops && (
                  <DriverCurrentRoute
                    stops={temporaryRoute.stops}
                    routeJid={typedRouteJid}
                  />
                )}
              </MapView>
            </div>
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>
    </APIProvider>
  )
}

export default AnalyzeOrderMap
